'use client'

import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import type { TopNavV6SubNavItem } from '@src/components/TopNavV6/TopNavV6Props'
import { cn } from '@src/utils/tailwind'

import { TopNavV6DesktopMiniMenu } from './TopNavV6DesktopMiniMenu'
import { TopNavV6DesktopThirdLevelMenu } from './TopNavV6DesktopThirdLevelMenu'

interface TopNavV6DesktopSubNavProps {
  items: TopNavV6SubNavItem[]
}

export const TopNavV6DesktopSubNav = ({ items }: TopNavV6DesktopSubNavProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      // Cleanup timeout on unmount
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [])

  if (items.length === 0) return null

  const hoveredItem = hoveredIndex !== null ? items[hoveredIndex] : null
  const thirdLevelMenu = hoveredItem?.navItems

  return (
    <div className="relative">
      <div
        className="relative z-40 w-full border-b border-bodi-gray-dark bg-bodi-black-text py-0"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), linear-gradient(90deg, rgba(22, 24, 25, 1) 0%, rgba(22, 24, 25, 1) 100%)',
        }}
        onMouseLeave={() => {
          // Clear hover state when leaving the subnav area
          // Use a small delay to allow moving to the third level menu without clearing
          leaveTimeoutRef.current = setTimeout(() => {
            setHoveredIndex(null)
          }, 100)
        }}
      >
        <div className="flex h-12 items-center gap-6 px-4">
          {items.map((item, index) => (
            <div
              key={`sub-nav-item-${index}`}
              className="relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => {
                // Only clear if not moving to third level menu or mini menu
                if (!item.navItems && !item.miniMenu) {
                  setHoveredIndex(null)
                }
              }}
            >
              <LocalizedLink
                href={item.href ?? '#'}
                onClick={item.onClick}
                className={cn(
                  'group flex h-full items-center justify-center border-0 border-solid border-bodi-gray-light px-0 py-4 transition-colors hover:underline',
                  {
                    'text-bodi-white hover:decoration-bodi-white': hoveredIndex === index,
                    'text-bodi-gray-butters hover:decoration-bodi-white': hoveredIndex !== index,
                  }
                )}
              >
                <span className="font-sans text-body-base leading-6 font-normal">{item.title}</span>
                {(item.navItems ?? item.miniMenu) && (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                    <ChevronDownIcon className="size-5" />
                  </div>
                )}
              </LocalizedLink>
              {/* Mini Menu positioned below this item */}
              {item.miniMenu && hoveredIndex === index && (
                <div
                  onMouseEnter={() => {
                    // Cancel any pending clear from subnav leave
                    if (leaveTimeoutRef.current) {
                      clearTimeout(leaveTimeoutRef.current)
                      leaveTimeoutRef.current = null
                    }
                  }}
                  onMouseLeave={() => {
                    if (leaveTimeoutRef.current) {
                      clearTimeout(leaveTimeoutRef.current)
                      leaveTimeoutRef.current = null
                    }
                    setHoveredIndex(null)
                  }}
                >
                  <TopNavV6DesktopMiniMenu
                    menu={item.miniMenu}
                    isVisible={hoveredIndex === index}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {thirdLevelMenu && (
        <div
          onMouseEnter={() => {
            // Cancel any pending clear from subnav leave
            // This allows smooth transition from subnav to third level menu
            if (leaveTimeoutRef.current) {
              clearTimeout(leaveTimeoutRef.current)
              leaveTimeoutRef.current = null
            }
          }}
          onMouseLeave={() => {
            if (leaveTimeoutRef.current) {
              clearTimeout(leaveTimeoutRef.current)
              leaveTimeoutRef.current = null
            }
            setHoveredIndex(null)
          }}
        >
          <TopNavV6DesktopThirdLevelMenu
            menu={thirdLevelMenu}
            subNavItem={hoveredItem!}
            isVisible={hoveredIndex !== null}
          />
        </div>
      )}
    </div>
  )
}
